import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    FaFileInvoice, FaFileAlt, FaTrashAlt, FaSearch, FaArrowLeft,
    FaUpload, FaComments, FaFileDownload, FaFolder, FaEdit, FaArrowRight, FaHome,
    FaEllipsisV, FaFolderPlus, FaUserShield, FaTimes, FaChevronRight, FaEye
} from "react-icons/fa";
import FilePreviewModal from "./FilePreviewModal";
import PaymentDetailsModal from "./PaymentDetailsModal";

const DocumentsManager = ({ userId, userName: initialUserName, hideBackButton = false }) => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [documentFile, setDocumentFile] = useState(null);
    const [invoiceFile, setInvoiceFile] = useState(null);
    const [registeredAgentFile, setRegisteredAgentFile] = useState(null);
    const [userInvoices, setUserInvoices] = useState([]);
    const [userDocuments, setUserDocuments] = useState([]);
    const [chatDocuments, setChatDocuments] = useState([]);
    const [registeredAgentDocuments, setRegisteredAgentDocuments] = useState([]);
    const [folders, setFolders] = useState([]);
    const [currentFolder, setCurrentFolder] = useState(null);
    const [folderPath, setFolderPath] = useState([]);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showCreateFolder, setShowCreateFolder] = useState(false);
    const [showMoveModal, setShowMoveModal] = useState(false);
    const [showRenameModal, setShowRenameModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState({ type: "", id: "" });
    const [itemToMove, setItemToMove] = useState({ type: "", id: "", name: "" });
    const [itemToRename, setItemToRename] = useState({ type: "", id: "", name: "" });
    const [activeTab, setActiveTab] = useState("documents");
    const [searchTerm, setSearchTerm] = useState("");
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [newFolderName, setNewFolderName] = useState("");
    const [newItemName, setNewItemName] = useState("");
    const [targetFolder, setTargetFolder] = useState("");
    const [previewFile, setPreviewFile] = useState(null);
    const [forceDelete, setForceDelete] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);


    useEffect(() => {
        if (error) {
            const timer = setTimeout(() => setError(null), 5000);
            return () => clearTimeout(timer);
        }
    }, [error]);

    const fetchUserDocuments = async () => {
        if (!userId) return;
        const token = localStorage.getItem("adminToken");
        if (!token) {
            navigate("/login");
            return;
        }

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/api/auth/user/${userId}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            if (!response.ok) throw new Error("Failed to fetch user documents");

            const data = await response.json();
            setUser(data.user);
            setUserInvoices(data.user.invoices || []);
            setUserDocuments(data.user.documents || []);
            setRegisteredAgentDocuments(data.user.registeredAgentDocuments || []);
            setFolders(data.user.folders || []);

            // Fetch active chat documents
            const chatDocResponse = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/api/chat/documents?userId=${userId}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );
            const activeChatDocs = chatDocResponse.ok ? await chatDocResponse.json() : [];

            // Fetch archived years
            const yearsResponse = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/api/chat/available-years?userId=${userId}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            let archivedChatDocs = [];
            if (yearsResponse.ok) {
                const years = await yearsResponse.json();

                // Fetch archived documents for each year
                for (const yearData of years) {
                    const archivedDocsResponse = await fetch(
                        `${process.env.REACT_APP_API_BASE_URL}/api/chat/archived-documents?userId=${userId}&year=${yearData.year}`,
                        {
                            headers: { Authorization: `Bearer ${token}` },
                        }
                    );
                    if (archivedDocsResponse.ok) {
                        const yearDocs = await archivedDocsResponse.json();
                        archivedChatDocs = [...archivedChatDocs, ...yearDocs];
                    }
                }
            }

            // Combine active and archived documents
            setChatDocuments([...activeChatDocs, ...archivedChatDocs]);
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUserDocuments();
        const interval = setInterval(fetchUserDocuments, 30000);
        return () => clearInterval(interval);
    }, [userId]);

    const handleFileChange = (e, type) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        const fileList = Array.from(files).map(file => ({
            name: file.name,
            size: file.size,
            type: file.type,
            file
        }));

        setSelectedFiles(fileList);

        if (type === 'invoice') {
            setInvoiceFile(files);
        } else if (type === 'registered-agent') {
            setRegisteredAgentFile(files);
        } else {
            setDocumentFile(files);
        }
    };

    const performInvoiceUpload = async (paymentData = null) => {
        setShowPaymentModal(false);
        const token = localStorage.getItem("adminToken");
        const formData = new FormData();

        setIsUploading(true);
        setUploadProgress(0);

        if (invoiceFile instanceof FileList) {
            Array.from(invoiceFile).forEach((file) => {
                formData.append('file', file);
            });
        }
        formData.append("userId", userId);
        if (currentFolder) {
            formData.append("folderId", currentFolder);
        }

        if (paymentData) {
            // Note: sendEmail might be inside paymentData if submitted via 'Add to History'
            // or passed separately if via 'Skip'
            const { sendEmail, ...details } = paymentData;
            formData.append("paymentDetails", JSON.stringify(details));
            formData.append("sendEmail", sendEmail !== undefined ? sendEmail : true);
        } else {
            formData.append("sendEmail", true);
        }

        try {
            const xhr = new XMLHttpRequest();
            xhr.upload.addEventListener('progress', (event) => {
                if (event.lengthComputable) {
                    const percentComplete = Math.round((event.loaded / event.total) * 100);
                    setUploadProgress(percentComplete);
                }
            });

            xhr.open("POST", `${process.env.REACT_APP_API_BASE_URL}/api/auth/send-invoice`);
            xhr.setRequestHeader("Authorization", `Bearer ${token}`);

            xhr.onload = () => {
                if (xhr.status === 200) {
                    try {
                        const data = JSON.parse(xhr.response);
                        setUserInvoices(data.user.invoices || []);
                        setFolders(data.user.folders || []);
                        setInvoiceFile(null);
                        setSelectedFiles([]);
                        setError("");
                    } catch (e) {
                        setError("Failed to parse server response");
                    }
                } else {
                    try {
                        const errorData = JSON.parse(xhr.response);
                        setError(errorData.message || "Failed to send invoice");
                    } catch (e) {
                        setError(`Failed to send invoice: ${xhr.statusText || xhr.status}`);
                    }
                }
                setIsUploading(false);
                setUploadProgress(0);
            };

            xhr.onerror = () => {
                setIsUploading(false);
                setUploadProgress(0);
                setError("Network error. Please check your connection.");
            };

            xhr.send(formData);
        } catch (error) {
            setIsUploading(false);
            setUploadProgress(0);
            setError(error.message);
        }
    };

    const handleInvoiceSubmit = async () => {
        if (!invoiceFile) {
            alert("Please select a file");
            return;
        }
        setShowPaymentModal(true);
    };

    const handleDocumentSubmit = async () => {
        if (!documentFile) {
            alert("Please select a file");
            return;
        }

        const token = localStorage.getItem("adminToken");
        const formData = new FormData();

        setIsUploading(true);
        setUploadProgress(0);

        if (documentFile instanceof FileList) {
            Array.from(documentFile).forEach((file) => {
                formData.append('file', file);
            });
        }
        formData.append("userId", userId);
        if (currentFolder) {
            formData.append("folderId", currentFolder);
        }

        try {
            const xhr = new XMLHttpRequest();
            xhr.upload.addEventListener('progress', (event) => {
                if (event.lengthComputable) {
                    const percentComplete = Math.round((event.loaded / event.total) * 100);
                    setUploadProgress(percentComplete);
                }
            });

            xhr.open("POST", `${process.env.REACT_APP_API_BASE_URL}/api/auth/send-document`);
            xhr.setRequestHeader("Authorization", `Bearer ${token}`);

            xhr.onload = () => {
                if (xhr.status === 200) {
                    try {
                        const data = JSON.parse(xhr.response);
                        setUserDocuments(data.user.documents || []);
                        setFolders(data.user.folders || []);
                        setDocumentFile(null);
                        setSelectedFiles([]);
                        setError("");
                    } catch (e) {
                        setError("Failed to parse server response");
                    }
                } else {
                    try {
                        const errorData = JSON.parse(xhr.response);
                        setError(errorData.message || "Failed to send document");
                    } catch (e) {
                        setError(`Failed to send document: ${xhr.statusText || xhr.status}`);
                    }
                }
                setIsUploading(false);
                setUploadProgress(0);
            };

            xhr.onerror = () => {
                setIsUploading(false);
                setUploadProgress(0);
                setError("Network error. Please check your connection.");
            };

            xhr.send(formData);
        } catch (error) {
            setIsUploading(false);
            setUploadProgress(0);
            setError(error.message);
        }
    };

    const handleRegisteredAgentSubmit = async () => {
        if (!registeredAgentFile) {
            alert("Please select a file");
            return;
        }

        const token = localStorage.getItem("adminToken");
        const formData = new FormData();

        setIsUploading(true);
        setUploadProgress(0);

        if (registeredAgentFile instanceof FileList) {
            Array.from(registeredAgentFile).forEach((file) => {
                formData.append('file', file);
            });
        }
        formData.append("userId", userId);
        if (currentFolder) {
            formData.append("folderId", currentFolder);
        }

        try {
            const xhr = new XMLHttpRequest();
            xhr.upload.addEventListener('progress', (event) => {
                if (event.lengthComputable) {
                    const percentComplete = Math.round((event.loaded / event.total) * 100);
                    setUploadProgress(percentComplete);
                }
            });

            xhr.open("POST", `${process.env.REACT_APP_API_BASE_URL}/api/auth/send-registered-agent-document`);
            xhr.setRequestHeader("Authorization", `Bearer ${token}`);

            xhr.onload = () => {
                if (xhr.status === 200) {
                    try {
                        const data = JSON.parse(xhr.response);
                        setRegisteredAgentDocuments(data.user.registeredAgentDocuments || []);
                        setFolders(data.user.folders || []);
                        setRegisteredAgentFile(null);
                        setSelectedFiles([]);
                        setError("");
                    } catch (e) {
                        setError("Failed to parse server response");
                    }
                } else {
                    try {
                        const errorData = JSON.parse(xhr.response);
                        setError(errorData.message || "Failed to send registered agent document");
                    } catch (e) {
                        setError(`Failed to send registered agent document: ${xhr.statusText || xhr.status}`);
                    }
                }
                setIsUploading(false);
                setUploadProgress(0);
            };

            xhr.onerror = () => {
                setIsUploading(false);
                setUploadProgress(0);
                setError("Network error. Please check your connection.");
            };

            xhr.send(formData);
        } catch (error) {
            setIsUploading(false);
            setUploadProgress(0);
            setError(error.message);
        }
    };

    const createFolder = async () => {
        if (!newFolderName.trim()) {
            alert("Please enter a folder name");
            return;
        }

        const token = localStorage.getItem("adminToken");
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/api/auth/create-folder`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        userId,
                        folderName: newFolderName,
                        parentFolderId: currentFolder
                    }),
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to create folder");
            }

            const data = await response.json();
            setFolders(data.user.folders || []);
            setNewFolderName("");
            setShowCreateFolder(false);
        } catch (error) {
            setError(error.message);
        }
    };

    const moveItem = async () => {
        if (!itemToMove.id || !targetFolder) return;

        const token = localStorage.getItem("adminToken");
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/api/auth/move-item`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        userId,
                        itemType: itemToMove.type,
                        itemId: itemToMove.id,
                        targetFolderId: targetFolder
                    }),
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to move item");
            }

            const data = await response.json();
            if (itemToMove.type === "invoice") {
                setUserInvoices(data.user.invoices || []);
            } else if (itemToMove.type === "document") {
                setUserDocuments(data.user.documents || []);
            } else if (itemToMove.type === "registered-agent-document") {
                setRegisteredAgentDocuments(data.user.registeredAgentDocuments || []);
            }
            setFolders(data.user.folders || []);
            setShowMoveModal(false);
            setItemToMove({ type: "", id: "", name: "" });
            setTargetFolder("");
        } catch (error) {
            setError(error.message);
        }
    };

    const renameItem = async () => {
        if (!itemToRename.id || !newItemName.trim()) return;

        const token = localStorage.getItem("adminToken");
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/api/auth/rename-item`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        userId,
                        itemType: itemToRename.type,
                        itemId: itemToRename.id,
                        newName: newItemName
                    }),
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to rename item");
            }

            const data = await response.json();
            if (itemToRename.type === "invoice") {
                setUserInvoices(data.user.invoices || []);
            } else if (itemToRename.type === "document") {
                setUserDocuments(data.user.documents || []);
            } else {
                setFolders(data.user.folders || []);
            }
            setShowRenameModal(false);
            setItemToRename({ type: "", id: "", name: "" });
            setNewItemName("");
        } catch (error) {
            setError(error.message);
        }
    };

    const deleteItem = async () => {
        const token = localStorage.getItem("adminToken");
        try {
            let response;
            if (itemToDelete.type === "invoice") {
                response = await fetch(
                    `${process.env.REACT_APP_API_BASE_URL}/api/auth/user/${userId}/invoice/${itemToDelete.id}`,
                    {
                        method: "DELETE",
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
            } else if (itemToDelete.type === "document") {
                response = await fetch(
                    `${process.env.REACT_APP_API_BASE_URL}/api/auth/user/${userId}/document/${itemToDelete.id}`,
                    {
                        method: "DELETE",
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
            } else if (itemToDelete.type === "registered-agent-document") {
                response = await fetch(
                    `${process.env.REACT_APP_API_BASE_URL}/api/auth/user/${userId}/registered-agent-document/${itemToDelete.id}`,
                    {
                        method: "DELETE",
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
            } else if (itemToDelete.type === "folder") {
                response = await fetch(
                    `${process.env.REACT_APP_API_BASE_URL}/api/auth/user/${userId}/folder/${itemToDelete.id}${forceDelete ? '?force=true' : ''}`,
                    {
                        method: "DELETE",
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
            }

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to delete item");
            }

            const data = await response.json();
            if (itemToDelete.type === "invoice") {
                setUserInvoices(data.user.invoices || []);
            } else if (itemToDelete.type === "document") {
                setUserDocuments(data.user.documents || []);
            } else if (itemToDelete.type === "registered-agent-document") {
                setRegisteredAgentDocuments(data.user.registeredAgentDocuments || []);
            }
            setFolders(data.user.folders || []);
            setShowDeleteConfirm(false);
        } catch (error) {
            setError(error.message);
        }
    };

    const openFolder = (folder) => {
        setCurrentFolder(folder._id);
        setFolderPath([...folderPath, folder]);
    };

    const navigateToFolder = (index) => {
        if (index === -1) {
            setCurrentFolder(null);
            setFolderPath([]);
        } else {
            const newPath = folderPath.slice(0, index + 1);
            setFolderPath(newPath);
            setCurrentFolder(newPath[newPath.length - 1]._id);
        }
    };

    const getCurrentFolderContents = () => {
        if (!currentFolder) return folders.filter(f => !f.parentFolder);
        return folders.filter(f => f.parentFolder === currentFolder);
    };

    const getCurrentFolderDocuments = () => {
        return userDocuments.filter(doc => doc.folderId === currentFolder);
    };

    const getCurrentFolderInvoices = () => {
        return userInvoices.filter(inv => inv.folderId === currentFolder);
    };

    const formatFileSize = (bytes) => {
        if (!bytes || bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const getCurrentFolderRegisteredAgentDocuments = () => {
        return registeredAgentDocuments.filter(doc => doc.folderId === currentFolder);
    };

    const getAllFolders = () => {
        return folders.filter(f => f._id !== currentFolder);
    };

    const [openFolderMenu, setOpenFolderMenu] = useState(null);

    const userName = user?.name || initialUserName;

    if (loading) {
        return (
            <div className="flex items-center justify-center p-12">
                <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="relative">
            {/* Error Banner */}
            {error && (
                <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[100] w-full max-w-xl animate-in fade-in slide-in-from-top-4 duration-300">
                    <div className="mx-4 p-4 bg-red-100 dark:bg-red-900/40 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded-2xl shadow-xl flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-red-200 dark:bg-red-800/50 flex items-center justify-center flex-shrink-0">
                                <FaTimes className="text-red-600 dark:text-red-400" />
                            </div>
                            <p className="text-sm font-medium">{error}</p>
                        </div>
                        <button 
                            onClick={() => setError(null)}
                            className="p-2 hover:bg-red-200/50 dark:hover:bg-red-800/50 rounded-xl transition-colors"
                        >
                            <FaTimes />
                        </button>
                    </div>
                </div>
            )}
            
            {!hideBackButton && (
                <button onClick={() => navigate(-1)} className="mb-6 flex items-center gap-2 px-4 py-2 border border-primary-500 text-primary-600 font-medium rounded-xl hover:bg-primary-50 dark:hover:bg-primary-900/20">
                    <FaArrowLeft /> Back
                </button>
            )}

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Documents for <span className="text-primary-600">{userName}</span>
                </h2>
                <div className="relative max-w-md w-full">
                    <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search files and folders..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white"
                    />
                </div>
            </div>

            {/* Breadcrumb Navigation */}
            <div className="flex items-center gap-2 mb-6 text-sm flex-wrap">
                <button onClick={() => navigateToFolder(-1)} className={`flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700 ${!currentFolder ? 'text-primary-600 font-medium' : 'text-gray-600 dark:text-gray-400'}`}>
                    <FaHome /> Root
                </button>
                {folderPath.map((folder, index) => (
                    <React.Fragment key={folder._id}>
                        <FaChevronRight className="text-gray-400 w-3 h-3" />
                        <button onClick={() => navigateToFolder(index)} className={`flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700 ${index === folderPath.length - 1 ? 'text-primary-600 font-medium' : 'text-gray-600 dark:text-gray-400'}`}>
                            <FaFolder /> {folder.name}
                        </button>
                    </React.Fragment>
                ))}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2 mb-6">
                <button onClick={() => setShowCreateFolder(true)} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white font-medium rounded-xl hover:bg-green-700">
                    <FaFolderPlus /> New Folder
                </button>
                <div className="flex rounded-xl overflow-hidden border border-gray-200 dark:border-dark-600">
                    <button onClick={() => setActiveTab("documents")} className={`flex items-center gap-2 px-4 py-2 font-medium ${activeTab === "documents" ? 'bg-gray-600 text-white' : 'bg-white dark:bg-dark-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700'}`}>
                        <FaFileAlt /> Documents
                    </button>
                    <button onClick={() => setActiveTab("invoices")} className={`flex items-center gap-2 px-4 py-2 font-medium ${activeTab === "invoices" ? 'bg-primary-600 text-white' : 'bg-white dark:bg-dark-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700'}`}>
                        <FaFileInvoice /> Invoices
                    </button>
                    <button onClick={() => setActiveTab("chat-documents")} className={`flex items-center gap-2 px-4 py-2 font-medium ${activeTab === "chat-documents" ? 'bg-cyan-600 text-white' : 'bg-white dark:bg-dark-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700'}`}>
                        <FaComments /> Chat
                    </button>
                    <button onClick={() => setActiveTab("registered-agent")} className={`flex items-center gap-2 px-4 py-2 font-medium ${activeTab === "registered-agent" ? 'bg-yellow-500 text-white' : 'bg-white dark:bg-dark-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700'}`}>
                        <FaUserShield /> Agent
                    </button>
                </div>
            </div>

            {/* Folders Section */}
            {getCurrentFolderContents().length > 0 && (
                <div className="mb-6 bg-white dark:bg-dark-800 rounded-2xl shadow-sm border border-gray-100 dark:border-dark-700 overflow-hidden">
                    <div className="p-4 border-b border-gray-100 dark:border-dark-700">
                        <h6 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2"><FaFolder className="text-yellow-500" /> Folders</h6>
                    </div>
                    <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                        {getCurrentFolderContents().map((folder) => (
                            <div key={folder._id} className="relative bg-gray-50 dark:bg-dark-700 rounded-xl p-4 flex flex-col items-center cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-600 transition-colors">
                                <div className="relative">
                                    <FaFolder size={48} className="text-yellow-500 mb-2" onClick={() => openFolder(folder)} />
                                    <button onClick={() => setOpenFolderMenu(openFolderMenu === folder._id ? null : folder._id)} className="absolute -top-1 -right-4 p-1 text-gray-400 hover:text-gray-600">
                                        <FaEllipsisV className="w-3 h-3" />
                                    </button>
                                    {openFolderMenu === folder._id && (
                                        <div className="absolute right-0 top-6 w-32 bg-white dark:bg-dark-800 rounded-lg shadow-lg border border-gray-100 dark:border-dark-700 z-10 py-1">
                                            <button onClick={() => { setItemToRename({ type: "folder", id: folder._id, name: folder.name }); setNewItemName(folder.name); setShowRenameModal(true); setOpenFolderMenu(null); }} className="w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700 flex items-center gap-2"><FaEdit /> Rename</button>
                                            <button onClick={() => { setItemToDelete({ type: "folder", id: folder._id }); setShowDeleteConfirm(true); setOpenFolderMenu(null); }} className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"><FaTrashAlt /> Delete</button>
                                        </div>
                                    )}
                                </div>
                                <span className="text-sm text-gray-700 dark:text-gray-300 text-center" onClick={() => openFolder(folder)}>{folder.name}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Documents Section */}
            {activeTab === "documents" && (
                <>
                    {/* Upload Document Card */}
                    <div className="mb-6 bg-white dark:bg-dark-800 rounded-2xl shadow-sm border border-gray-100 dark:border-dark-700 overflow-hidden">
                        <div className="p-4 border-b border-gray-100 dark:border-dark-700">
                            <h6 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2"><FaUpload /> Upload Documents</h6>
                        </div>
                        <div className="p-4">
                            <input type="file" onChange={(e) => handleFileChange(e, 'document')} multiple disabled={isUploading} className="w-full px-4 py-2 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-primary-100 file:text-primary-700 dark:file:bg-primary-900/30 dark:file:text-primary-400" />
                            {selectedFiles.length > 0 && (
                                <div className="mt-4">
                                    <h6 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Selected Files:</h6>
                                    <div className="space-y-2">
                                        {selectedFiles.map((file, index) => (
                                            <div key={index} className="flex justify-between items-center p-3 bg-gray-50 dark:bg-dark-700 rounded-lg">
                                                <span className="flex items-center gap-2 text-gray-700 dark:text-gray-300"><FaFileAlt className="text-gray-400" /> {file.name}</span>
                                                <span className="px-2 py-1 text-xs bg-gray-200 dark:bg-dark-600 text-gray-600 dark:text-gray-400 rounded">{formatFileSize(file.size)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {isUploading && (
                                <div className="mt-4">
                                    <div className="flex justify-between text-sm text-gray-600 mb-1"><span>Uploading...</span><span>{uploadProgress}%</span></div>
                                    <div className="h-2 bg-gray-200 dark:bg-dark-600 rounded-full overflow-hidden"><div className="h-full bg-gray-600 transition-all" style={{ width: `${uploadProgress}%` }} /></div>
                                </div>
                            )}
                            <button onClick={handleDocumentSubmit} disabled={!documentFile || isUploading} className="mt-4 flex items-center gap-2 px-4 py-2 bg-gray-600 text-white font-medium rounded-xl hover:bg-gray-700 disabled:opacity-50">
                                {isUploading ? 'Uploading...' : <><FaUpload /> Upload Documents</>}
                            </button>
                        </div>
                    </div>

                    {/* Document List */}
                    <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-sm border border-gray-100 dark:border-dark-700 overflow-hidden">
                        <div className="p-4 border-b border-gray-100 dark:border-dark-700">
                            <h6 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2"><FaFileAlt /> Documents</h6>
                        </div>
                        {getCurrentFolderDocuments().length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50 dark:bg-dark-700">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Name</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Size</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Date</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {getCurrentFolderDocuments().map((document, index) => (
                                            <tr key={index} className="border-b border-gray-100 dark:border-dark-700 hover:bg-gray-50 dark:hover:bg-dark-700">
                                                <td className="px-4 py-3 text-gray-700 dark:text-gray-300 flex items-center gap-2"><FaFileAlt className="text-gray-400" /> {document.originalName}</td>
                                                <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-sm">{formatFileSize(document.size || 0)}</td>
                                                <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-sm">{new Date(document.date).toLocaleDateString()}</td>
                                                <td className="px-4 py-3">
                                                    <div className="flex gap-1">
                                                        <button
                                                            onClick={() => setPreviewFile({
                                                                url: document.fileUrl,
                                                                name: document.originalName,
                                                                size: document.size,
                                                                type: document.originalName?.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'image/jpeg'
                                                            })}
                                                            className="p-2 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20"
                                                            title="Preview"
                                                        >
                                                            <FaEye />
                                                        </button>
                                                        <a href={`${process.env.REACT_APP_API_BASE_URL}${document.fileUrl}`} target="_blank" rel="noreferrer" className="p-2 text-primary-600 border border-primary-300 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20" title="Download"><FaFileDownload /></a>
                                                        <button onClick={() => { setItemToMove({ type: "document", id: document._id, name: document.originalName }); setShowMoveModal(true); }} className="p-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700" title="Move item"><FaArrowRight /></button>
                                                        <button onClick={() => { setItemToDelete({ type: "document", id: document._id }); setShowDeleteConfirm(true); }} className="p-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20" title="Delete item"><FaTrashAlt /></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="py-12 text-center">
                                <FaFileAlt className="mx-auto text-gray-300 dark:text-gray-600 text-5xl mb-3" />
                                <p className="text-gray-500">No documents in this folder</p>
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Invoices Section */}
            {activeTab === "invoices" && (
                <>
                    <div className="mb-6 bg-white dark:bg-dark-800 rounded-2xl shadow-sm border border-gray-100 dark:border-dark-700 overflow-hidden">
                        <div className="p-4 border-b border-gray-100 dark:border-dark-700">
                            <h6 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2"><FaUpload /> Upload Invoices</h6>
                        </div>
                        <div className="p-4">
                            <input type="file" onChange={(e) => handleFileChange(e, 'invoice')} multiple disabled={isUploading} className="w-full px-4 py-2 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-primary-100 file:text-primary-700" />
                            {selectedFiles.length > 0 && (
                                <div className="mt-4 space-y-2">
                                    {selectedFiles.map((file, index) => (
                                        <div key={index} className="flex justify-between items-center p-3 bg-gray-50 dark:bg-dark-700 rounded-lg">
                                            <span className="flex items-center gap-2 text-gray-700 dark:text-gray-300"><FaFileInvoice className="text-primary-500" /> {file.name}</span>
                                            <span className="px-2 py-1 text-xs bg-primary-100 text-primary-700 rounded">{formatFileSize(file.size)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {isUploading && (
                                <div className="mt-4">
                                    <div className="h-2 bg-gray-200 dark:bg-dark-600 rounded-full overflow-hidden"><div className="h-full bg-primary-600 transition-all" style={{ width: `${uploadProgress}%` }} /></div>
                                </div>
                            )}
                            <button onClick={handleInvoiceSubmit} disabled={!invoiceFile || isUploading} className="mt-4 flex items-center gap-2 px-4 py-2 bg-primary-600 text-white font-medium rounded-xl hover:bg-primary-700 disabled:opacity-50">
                                {isUploading ? 'Uploading...' : <><FaUpload /> Upload Invoices</>}
                            </button>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-sm border border-gray-100 dark:border-dark-700 overflow-hidden">
                        <div className="p-4 border-b border-gray-100 dark:border-dark-700">
                            <h6 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2"><FaFileInvoice /> Invoices</h6>
                        </div>
                        {getCurrentFolderInvoices().length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50 dark:bg-dark-700">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Name</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Size</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Date</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {getCurrentFolderInvoices().map((invoice, index) => (
                                            <tr key={index} className="border-b border-gray-100 dark:border-dark-700 hover:bg-gray-50 dark:hover:bg-dark-700">
                                                <td className="px-4 py-3 text-gray-700 dark:text-gray-300 flex items-center gap-2"><FaFileInvoice className="text-primary-500" /> {invoice.originalName}</td>
                                                <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-sm">{formatFileSize(invoice.size || 0)}</td>
                                                <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-sm">{new Date(invoice.date).toLocaleDateString()}</td>
                                                <td className="px-4 py-3">
                                                    <div className="flex gap-1">
                                                        <button
                                                            onClick={() => setPreviewFile({
                                                                url: invoice.fileUrl,
                                                                name: invoice.originalName,
                                                                size: invoice.size,
                                                                type: invoice.originalName?.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'image/jpeg'
                                                            })}
                                                            className="p-2 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20"
                                                            title="Preview"
                                                        >
                                                            <FaEye />
                                                        </button>
                                                        <a href={`${process.env.REACT_APP_API_BASE_URL}${invoice.fileUrl}`} target="_blank" rel="noreferrer" className="p-2 text-primary-600 border border-primary-300 rounded-lg hover:bg-primary-50"><FaFileDownload /></a>
                                                        <button onClick={() => { setItemToMove({ type: "invoice", id: invoice._id, name: invoice.originalName }); setShowMoveModal(true); }} className="p-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"><FaArrowRight /></button>
                                                        <button onClick={() => { setItemToDelete({ type: "invoice", id: invoice._id }); setShowDeleteConfirm(true); }} className="p-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50"><FaTrashAlt /></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="py-12 text-center">
                                <FaFileInvoice className="mx-auto text-gray-300 dark:text-gray-600 text-5xl mb-3" />
                                <p className="text-gray-500">No invoices in this folder</p>
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Registered Agent Documents Section */}
            {activeTab === "registered-agent" && (
                <>
                    <div className="mb-6 bg-white dark:bg-dark-800 rounded-2xl shadow-sm border border-gray-100 dark:border-dark-700 overflow-hidden">
                        <div className="p-4 border-b border-gray-100 dark:border-dark-700">
                            <h6 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2"><FaUpload /> Upload Registered Agent Documents</h6>
                        </div>
                        <div className="p-4">
                            <input type="file" onChange={(e) => handleFileChange(e, 'registered-agent')} multiple disabled={isUploading} className="w-full px-4 py-2 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-yellow-100 file:text-yellow-700" />
                            {selectedFiles.length > 0 && (
                                <div className="mt-4 space-y-2">
                                    {selectedFiles.map((file, index) => (
                                        <div key={index} className="flex justify-between items-center p-3 bg-gray-50 dark:bg-dark-700 rounded-lg">
                                            <span className="flex items-center gap-2 text-gray-700 dark:text-gray-300"><FaUserShield className="text-yellow-500" /> {file.name}</span>
                                            <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded">{formatFileSize(file.size)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {isUploading && (
                                <div className="mt-4">
                                    <div className="h-2 bg-gray-200 dark:bg-dark-600 rounded-full overflow-hidden"><div className="h-full bg-yellow-500 transition-all" style={{ width: `${uploadProgress}%` }} /></div>
                                </div>
                            )}
                            <button onClick={handleRegisteredAgentSubmit} disabled={!registeredAgentFile || isUploading} className="mt-4 flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white font-medium rounded-xl hover:bg-yellow-600 disabled:opacity-50">
                                {isUploading ? 'Uploading...' : <><FaUpload /> Upload Registered Agent Documents</>}
                            </button>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-sm border border-gray-100 dark:border-dark-700 overflow-hidden">
                        <div className="p-4 border-b border-gray-100 dark:border-dark-700">
                            <h6 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2"><FaUserShield /> Registered Agent Documents</h6>
                        </div>
                        {getCurrentFolderRegisteredAgentDocuments().length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50 dark:bg-dark-700">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Name</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Size</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Date</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {getCurrentFolderRegisteredAgentDocuments().map((document, index) => (
                                            <tr key={index} className="border-b border-gray-100 dark:border-dark-700 hover:bg-gray-50 dark:hover:bg-dark-700">
                                                <td className="px-4 py-3 text-gray-700 dark:text-gray-300 flex items-center gap-2"><FaUserShield className="text-yellow-500" /> {document.originalName}</td>
                                                <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-sm">{formatFileSize(document.size || 0)}</td>
                                                <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-sm">{new Date(document.date).toLocaleDateString()}</td>
                                                <td className="px-4 py-3">
                                                    <div className="flex gap-1">
                                                        <button
                                                            onClick={() => setPreviewFile({
                                                                url: document.fileUrl,
                                                                name: document.originalName,
                                                                size: document.size,
                                                                type: document.originalName?.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'image/jpeg'
                                                            })}
                                                            className="p-2 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20"
                                                            title="Preview"
                                                        >
                                                            <FaEye />
                                                        </button>
                                                        <a href={`${process.env.REACT_APP_API_BASE_URL}${document.fileUrl}`} target="_blank" rel="noreferrer" className="p-2 text-primary-600 border border-primary-300 rounded-lg hover:bg-primary-50"><FaFileDownload /></a>
                                                        <button onClick={() => { setItemToMove({ type: "registered-agent-document", id: document._id, name: document.originalName }); setShowMoveModal(true); }} className="p-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"><FaArrowRight /></button>
                                                        <button onClick={() => { setItemToDelete({ type: "registered-agent-document", id: document._id }); setShowDeleteConfirm(true); }} className="p-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50"><FaTrashAlt /></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="py-12 text-center">
                                <FaUserShield className="mx-auto text-gray-300 dark:text-gray-600 text-5xl mb-3" />
                                <p className="text-gray-500">No registered agent documents in this folder</p>
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Chat Documents Section */}
            {activeTab === "chat-documents" && (
                <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-sm border border-gray-100 dark:border-dark-700 overflow-hidden">
                    <div className="p-4 border-b border-gray-100 dark:border-dark-700">
                        <h6 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2"><FaComments /> Chat Documents</h6>
                    </div>
                    {chatDocuments.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50 dark:bg-dark-700">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Name</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Size</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Date</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {chatDocuments.map((document, index) => (
                                        <tr key={index} className="border-b border-gray-100 dark:border-dark-700 hover:bg-gray-50 dark:hover:bg-dark-700">
                                            <td className="px-4 py-3 text-gray-700 dark:text-gray-300 flex items-center gap-2"><FaFileAlt className="text-cyan-500" /> {document.originalName}</td>
                                            <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-sm">{formatFileSize(document.size || 0)}</td>
                                            <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-sm">{new Date(document.timestamp).toLocaleDateString()}</td>
                                            <td className="px-4 py-3">
                                                <div className="flex gap-1">
                                                    <button
                                                        onClick={() => setPreviewFile({
                                                            url: `/uploads/chat/${document.fileUrl.split('/').pop()}`,
                                                            name: document.originalName,
                                                            size: document.size,
                                                            type: document.originalName?.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'image/jpeg'
                                                        })}
                                                        className="p-2 text-cyan-600 border border-cyan-300 rounded-lg hover:bg-cyan-50 dark:hover:bg-cyan-900/20"
                                                        title="Preview"
                                                    >
                                                        <FaEye />
                                                    </button>
                                                    <a href={`${process.env.REACT_APP_API_BASE_URL}/uploads/chat/${document.fileUrl.split('/').pop()}`} target="_blank" rel="noreferrer" className="p-2 text-primary-600 border border-primary-300 rounded-lg hover:bg-primary-50 inline-flex" title="Download"><FaFileDownload /></a>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="py-12 text-center">
                            <FaComments className="mx-auto text-gray-300 dark:text-gray-600 text-5xl mb-3" />
                            <p className="text-gray-500">No chat documents available</p>
                        </div>
                    )}
                </div>
            )}

            {/* Create Folder Modal */}
            {showCreateFolder && (
                <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/50" onClick={() => setShowCreateFolder(false)} />
                    <div className="relative bg-white dark:bg-dark-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                        <div className="p-4 border-b border-gray-100 dark:border-dark-700 flex items-center justify-between">
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Create New Folder</h2>
                            <button onClick={() => setShowCreateFolder(false)} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"><FaTimes /></button>
                        </div>
                        <div className="p-6">
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Folder Name</label>
                            <input type="text" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} placeholder="Enter folder name" className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white" />
                        </div>
                        <div className="p-4 border-t border-gray-100 dark:border-dark-700 flex justify-end gap-2">
                            <button onClick={() => setShowCreateFolder(false)} className="px-4 py-2 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-100 dark:hover:bg-dark-600">Cancel</button>
                            <button onClick={createFolder} className="px-4 py-2 bg-primary-600 text-white font-medium rounded-xl hover:bg-primary-700">Create Folder</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Rename Modal */}
            {showRenameModal && (
                <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/50" onClick={() => setShowRenameModal(false)} />
                    <div className="relative bg-white dark:bg-dark-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                        <div className="p-4 border-b border-gray-100 dark:border-dark-700 flex items-center justify-between">
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Rename Item</h2>
                            <button onClick={() => setShowRenameModal(false)} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"><FaTimes /></button>
                        </div>
                        <div className="p-6">
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">New Name</label>
                            <input type="text" value={newItemName} onChange={(e) => setNewItemName(e.target.value)} placeholder="Enter new name" className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white" />
                        </div>
                        <div className="p-4 border-t border-gray-100 dark:border-dark-700 flex justify-end gap-2">
                            <button onClick={() => setShowRenameModal(false)} className="px-4 py-2 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-100 dark:hover:bg-dark-600">Cancel</button>
                            <button onClick={renameItem} className="px-4 py-2 bg-primary-600 text-white font-medium rounded-xl hover:bg-primary-700">Rename</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/50" onClick={() => { setShowDeleteConfirm(false); setForceDelete(false); }} />
                    <div className="relative bg-white dark:bg-dark-800 rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
                        <div className="p-4 border-b border-gray-100 dark:border-dark-700 flex items-center justify-between">
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Confirm Delete</h2>
                            <button onClick={() => { setShowDeleteConfirm(false); setForceDelete(false); }} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"><FaTimes /></button>
                        </div>
                        <div className="p-6">
                            <p className="text-gray-700 dark:text-gray-300 mb-6">Are you sure you want to remove this {itemToDelete.type?.replace(/-/g, ' ')}? This action cannot be undone.</p>
                            
                            {itemToDelete.type === "folder" && (
                                <div className="p-4 bg-primary-50 dark:bg-primary-900/10 border border-primary-100 dark:border-primary-800/50 rounded-2xl">
                                    <label className="flex items-start gap-3 cursor-pointer group">
                                        <div className="relative flex items-center mt-0.5">
                                            <input
                                                type="checkbox"
                                                className="peer h-5 w-5 cursor-pointer appearance-none rounded-md border border-gray-300 dark:border-dark-600 transition-all checked:bg-primary-600 checked:border-primary-600 focus:outline-none"
                                                checked={forceDelete}
                                                onChange={(e) => setForceDelete(e.target.checked)}
                                            />
                                            <svg className="absolute h-3.5 w-3.5 left-0.5 top-0.5 pointer-events-none opacity-0 peer-checked:opacity-100 transition-opacity text-white" viewBox="0 0 17 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M1 5.917L5.724 10.5L16 1" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </div>
                                        <div className="flex flex-col gap-0.5">
                                            <span className="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-primary-600 transition-colors">Recursive Delete</span>
                                            <span className="text-xs text-gray-500 dark:text-gray-400 italic">Delete all files and sub-folders inside this folder automatically.</span>
                                        </div>
                                    </label>
                                </div>
                            )}
                        </div>
                        <div className="p-4 border-t border-gray-100 dark:border-dark-700 flex justify-end gap-2">
                            <button onClick={() => { setShowDeleteConfirm(false); setForceDelete(false); }} className="px-4 py-2 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-100 dark:hover:bg-dark-600">Cancel</button>
                            <button onClick={() => { deleteItem(); setForceDelete(false); }} className="px-4 py-2 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 shadow-lg shadow-red-200 dark:shadow-none">Delete</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Move Item Modal */}
            {showMoveModal && (
                <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/50" onClick={() => setShowMoveModal(false)} />
                    <div className="relative bg-white dark:bg-dark-800 rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
                        <div className="p-4 border-b border-gray-100 dark:border-dark-700 flex items-center justify-between">
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Move Item</h2>
                            <button onClick={() => setShowMoveModal(false)} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"><FaTimes /></button>
                        </div>
                        <div className="p-6">
                            <p className="text-gray-700 dark:text-gray-300 mb-4">Move <strong>{itemToMove.name}</strong> to:</p>
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Select Destination Folder</label>
                            <select value={targetFolder} onChange={(e) => setTargetFolder(e.target.value)} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white">
                                <option value="">Root Folder</option>
                                {getAllFolders().map(folder => (<option key={folder._id} value={folder._id}>{folder.name}</option>))}
                            </select>
                        </div>
                        <div className="p-4 border-t border-gray-100 dark:border-dark-700 flex justify-end gap-2">
                            <button onClick={() => setShowMoveModal(false)} className="px-4 py-2 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-100 dark:hover:bg-dark-600">Cancel</button>
                            <button onClick={moveItem} className="px-4 py-2 bg-primary-600 text-white font-medium rounded-xl hover:bg-primary-700">Move</button>
                        </div>
                    </div>
                </div>
            )}

            {/* File Preview Modal */}
            {previewFile && (
                <FilePreviewModal
                    file={previewFile}
                    onClose={() => setPreviewFile(null)}
                />
            )}

            {/* Payment Details Modal */}
            <PaymentDetailsModal
                isOpen={showPaymentModal}
                onClose={() => setShowPaymentModal(false)}
                onSubmit={(details) => performInvoiceUpload(details)}
                onSkip={(sendEmail) => performInvoiceUpload({ sendEmail })}
                userName={userName}
                isLoading={isUploading}
            />
        </div>
    );
};

export default DocumentsManager;
